"use client";

/**
 * Terms & Conditions page.
 *
 * Pulls the CMS entry whose URL in the admin is:
 *   /cms/cms-6d2aba60-bd91-4c2d-885f-06c37d212964/
 *     entry/cmse-c676f5f8-aaba-44d8-8a0d-aaeed905c668/
 *     data/cmsd-9b60f11d-2c86-4595-9b31-d2fa64b6ff41
 *
 * The cmsd-* row is the per-language data row — the
 * `/api/cms/entry/dataByCmsAndLanguage` endpoint already filters those
 * by the current language, so we just need the cms uuid + entry uuid
 * to pick the right entry on the client side. Falls back to en_US when
 * the requested locale has no published row (mirrors the
 * continent/city CMS-data behaviour).
 */
import React, { FC, useEffect, useState } from "react";
import ReactHtmlParser from "react-html-parser";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { getCmsEntryData } from "@/app/lib/reducers/cmsEntryDataReducer";
import { useTranslation } from "react-i18next";
import type { CmsEntry } from "@/data/types";

// Hard-coded UUIDs — these CMS records live in the back-office and don't
// change. Keeping the constants here makes the lookup readable and
// avoids a round-trip through an env var.
const TERMS_CMS_UUID = "cms-6d2aba60-bd91-4c2d-885f-06c37d212964";
const TERMS_ENTRY_UUID = "cmse-c676f5f8-aaba-44d8-8a0d-aaeed905c668";

const TermsAndConditionsPage: FC = () => {
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();
  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage || "fr_FR"
  );
  const entries: CmsEntry[] | null = useAppSelector(
    (s) => s.cmsEntryData?.cmsEntryData
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedLanguage) return;
    setLoading(true);
    Promise.resolve(
      (dispatch as any)(getCmsEntryData(TERMS_CMS_UUID, selectedLanguage))
    ).finally(() => setLoading(false));
  }, [dispatch, selectedLanguage]);

  // Pick the specific entry by uuid; the response holds ALL entries for
  // the cms container so we filter client-side. `data[0]` is the row
  // matching the requested language (server already filtered).
  const entry = (entries || []).find(
    (e) => e.content_cms_entry__ === TERMS_ENTRY_UUID
  );
  const cmsData = entry?.data?.[0];

  const title =
    cmsData?.title ||
    (t("terms_and_conditions_title", {
      defaultValue: "Conditions générales d'utilisation",
    }) as string);

  return (
    <div className="gdx" style={{ minHeight: "60vh" }}>
      <div
        style={{
          maxWidth: 920,
          margin: "0 auto",
          padding: "clamp(28px, 4vw, 64px) clamp(20px, 4vw, 56px)",
        }}
      >
        <h1
          className="font-extrabold mb-6"
          style={{
            color: "var(--g-burgundy)",
            fontSize: "clamp(28px, 4vw, 42px)",
            lineHeight: 1.1,
          }}
        >
          {title}
        </h1>

        {loading && !cmsData && (
          <div
            aria-hidden="true"
            style={{
              minHeight: 240,
              background: "var(--g-bg-soft)",
              borderRadius: 16,
            }}
          />
        )}

        {!loading && !cmsData && (
          <p style={{ color: "var(--g-ink-500)", fontSize: 14 }}>
            {t("terms_and_conditions_unavailable", {
              defaultValue:
                "Le contenu n'est pas encore disponible dans cette langue.",
            })}
          </p>
        )}

        {cmsData?.content && (
          <article
            className="cms-content"
            style={{
              color: "var(--g-ink-700)",
              fontSize: 15,
              lineHeight: 1.65,
            }}
          >
            {ReactHtmlParser(cmsData.content)}
          </article>
        )}
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;
